<template>
  <aside class="sidebar-panel">
    <!-- Station Information -->
    <StationInfo
      :station="currentStation"
      :candidate="currentCandidate"
      :is-flagged="isCurrentCandidateFlagged"
      @toggle-flag="toggleCandidateFlag"
    />
    
    <!-- Timer Component -->
    <TimerComponent
      :time-remaining="timeRemaining"
      :total-time="currentStation.duration"
      :is-paused="isTimerPaused"
      @timer-pause="pauseTimer"
      @timer-resume="resumeTimer"
      @timer-expired="handleTimerExpired"
    />
    
    <!-- Station Navigation -->
    <StationNavigation
      :stations="stations"
      :current-station-id="currentStation.id"
      :completion-status="stationCompletionStatus"
      @station-select="changeStation"
    />
    
    <!-- Candidate List -->
    <CandidateList
      :candidates="filteredCandidates"
      :current-candidate-id="currentCandidate.id"
      :completion-data="candidateCompletionData"
      @candidate-select="changeCandidate"
    />
    
    <!-- Quick Actions -->
    <QuickActions
      @quick-save="quickSave"
      @add-note="openNoteModal"
      @request-help="requestHelp"
    />
  </aside>
</template>

<script>
import StationInfo from "src/components/OSCE/contraptions/StationInfo.vue"
import TimerComponent from "src/components/OSCE/contraptions/TimerComponent.vue"
import StationNavigation from "src/components/OSCE/contraptions/StationNavigation.vue"
import CandidateList from "src/components/OSCE/contraptions/CandidateList.vue"
import QuickActions from "src/components/OSCE/contraptions/QuickActions.vue"


export default {
  name: 'OSCEHelper',
  components: {
    StationInfo,
    TimerComponent,
    StationNavigation,
    CandidateList,
    QuickActions
  },
  props: {
    currentStation: {
      type: Object,
      required: true
    },
    currentCandidate: {
      type: Object,
      required: true
    },
    stations: {
      type: Array,
      default: () => []
    },
    candidates: {
      type: Array,
      default: () => []
    },
    timeRemaining: {
      type: Number,
      required: true
    },
    scores: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isTimerPaused: false,
      stationCompletionStatus: {},
      candidateCompletionData: {}
    }
  },
  
  computed: {
    // Filter candidates for current station only
    filteredCandidates() {
      return this.candidates.filter(candidate => 
        candidate.station === this.currentStation.id
      );
    },
    
    // Check if current candidate is flagged
    isCurrentCandidateFlagged() {
      return this.currentCandidate.flagged || false;
    },
    
    // Calculate completion percentage for progress indicators
    completionPercentage() {
      if (!this.currentStation.criteria) return 0;
      const total = this.currentStation.criteria.length;
      const completed = Object.keys(this.scores).length;
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    }
  },
  
  watch: {
    // Update completion data when scores change
    scores: {
      handler(newScores) {
        console.log(newScores)
        this.updateCompletionData();
      },
      deep: true
    },
    
    // Update when station changes
    currentStation: {
      handler(newStation) {
        console.log(newStation)
        this.updateStationCompletionStatus();
      },
      deep: true
    }
  },
  
  mounted() {
    this.initializeCompletionData();
    this.setupKeyboardShortcuts();
  },
  
  methods: {
    initializeCompletionData() {
      this.updateStationCompletionStatus();
      this.updateCompletionData();
    },
    
    // Station completion tracking
    updateStationCompletionStatus() {
      this.stationCompletionStatus = {};
      this.stations.forEach(station => {
        // In real app, this would check actual completion data
        this.stationCompletionStatus[station.id] = {
          completed: Math.random() > 0.5, // Mock data
          visited: station.id === this.currentStation.id
        };
      });
    },
    
    // Candidate completion tracking
    updateCompletionData() {
      this.candidateCompletionData = {};
      this.filteredCandidates.forEach(candidate => {
        const completion = this.calculateCandidateCompletion(candidate.id);
        this.candidateCompletionData[candidate.id] = {
          percentage: completion.percentage,
          isComplete: completion.percentage >= 95, // 95% threshold
          hasNotes: completion.hasNotes,
          lastUpdated: completion.lastUpdated
        };
      });
    },
    
    calculateCandidateCompletion(candidateId) {
        console.log(candidateId)
      // Mock implementation - in real app, calculate based on actual scores
      const completedCriteria = Object.keys(this.scores).length;
      const totalCriteria = this.currentStation.criteria?.length || 1;
      const percentage = Math.min(100, Math.round((completedCriteria / totalCriteria) * 100));
      
      return {
        percentage,
        hasNotes: Object.values(this.scores).some(score => score.comments && score.comments.trim() !== ''),
        lastUpdated: new Date().toISOString()
      };
    },
    
    // Station navigation
    changeStation(stationId) {
      if (stationId === this.currentStation.id) return;
      
      this.$emit('station-change', stationId);
    },
    
    // Candidate navigation
    changeCandidate(candidateId) {
      if (candidateId === this.currentCandidate.id) return;
      
      this.$emit('candidate-change', candidateId);
    },
    
    // Timer controls
    pauseTimer() {
      this.isTimerPaused = true;
      this.$emit('timer-paused');
    },
    
    resumeTimer() {
      this.isTimerPaused = false;
      this.$emit('timer-resumed');
    },
    
    handleTimerExpired() {
      this.$emit('timer-expired');
    },
    
    // Candidate flagging
    toggleCandidateFlag() {
      this.$emit('candidate-flag-toggle', {
        candidateId: this.currentCandidate.id,
        flagged: !this.isCurrentCandidateFlagged
      });
    },
    
    // Quick actions
    quickSave() {
      this.$emit('quick-save');
    },
    
    openNoteModal() {
      this.$emit('open-note-modal', {
        station: this.currentStation,
        candidate: this.currentCandidate
      });
    },
    
    requestHelp() {
      this.$emit('request-help', {
        station: this.currentStation.name,
        candidate: this.currentCandidate.name,
        timestamp: new Date().toISOString()
      });
    },
    
    // Keyboard shortcuts for efficiency
    setupKeyboardShortcuts() {
      const handleKeyPress = (event) => {
        // Only handle if not in input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        
        // Number keys 1-5 for quick station navigation
        if (event.key >= '1' && event.key <= '5') {
          const stationIndex = parseInt(event.key) - 1;
          if (stationIndex < this.stations.length) {
            event.preventDefault();
            this.changeStation(this.stations[stationIndex].id);
          }
        }
        
        // Spacebar to pause/resume timer
        if (event.code === 'Space') {
          event.preventDefault();
          if (this.isTimerPaused) {
            this.resumeTimer();
          } else {
            this.pauseTimer();
          }
        }
      };
      
      document.addEventListener('keydown', handleKeyPress);
      this.$once('hook:beforeDestroy', () => {
        document.removeEventListener('keydown', handleKeyPress);
      });
    }
  }
}
</script>

<style scoped>
.sidebar-panel {
  min-width: 320px;
  padding: 20px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
}

/* Responsive design */
@media (max-width: 1024px) {
  .sidebar-panel {
    width: 280px;
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .sidebar-panel {
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }
}
</style>